import { Box, Image, Typography, Flex } from "@jewlr/storybook/core"
import { FocusScope } from "@react-aria/focus"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import styled from "styled-components"

import { updateDiamond, updateTmpDiamond } from "areas/product/store/actions"
import {
  ChoiceOption,
  OptionTitle,
  OptionPrice,
} from "areas/product/wizard-options/components"
import { jewlr_t } from "helpers/application"
import { to_currency } from "helpers/number"
import { useNavigation } from "helpers/use-navigation"

import { Tag } from "./components"

const CustomizeStepTitle = styled(Typography).attrs((props) => ({
  as: "h5",
  fontSize: "22px",
  mb: 2,
  ...props.theme.diamondSelector.customizeStepTitle,
}))``
const ChoiceOptionWrapper = styled.div`
  ${(props) =>
    props.showScroll &&
    `
  position: relative;
  :before,
  :after {
    display: none;
    content: " ";
    position: absolute;
    top: 0;
    height: calc(100% - 10px);
    width: 60px;
    z-index: 1;
   background: linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 50%, rgba(255,255,255,0) 100%)

  }
  ${props.theme.mediaQueries.mobileOnly`
    :before {
      display: block;
      left:-46px;
    }
    :after {
      display: block;
      right:-44px;
    }
  `}
  `}
`
const ChoiceOptionFlex = styled(Flex).attrs((props) => ({
  ...props,
}))`
  padding-top: 8px;
  position: relative;

  ${(props) => props.theme.mediaQueries.mobileOnly`
  ${
    props.showScroll &&
    `
     margin-right: -16px;
      overflow-x: auto;
      padding-left: 12px;
      &:last-child:after {
        content: "";
        flex: 0 0 36px;
      }
  `
  }
  overflow-x: scroll;
  -webkit-overflow-scrolling: touch;

`}
`
const Description = styled(Typography).attrs((props) => ({
  fontSize: "15px",
  ...props.theme.diamondSelector.customizeDescription,
}))``

const ChoiceOptions = ({ opt, selectedOption, selectedShape, setChoice }) => {
  const keyboardProps = useNavigation({ direction: "horizontal" })
  const selected = selectedOption === opt.value

  return (
    <ChoiceOption
      aria-checked={selected}
      mx="auto"
      onClick={() => setChoice(opt)}
      role="radio"
      selected={selected}
      tabIndex={selected ? 0 : -1}
      width={{ _: "88px", desktop: "110px", tablet: "100px" }}
      {...keyboardProps}
    >
      <Image
        alt={selectedShape}
        height="60px"
        src={`${sl.config.jewlr_asset_url}images/product/diamond/${selectedShape}/1.00ct.svg`}
        width="60px"
      />
      <OptionTitle bold color="black" mb="6px">
        {opt.name}
      </OptionTitle>

      <OptionPrice>
        {opt.display_price ? (
          to_currency(opt.display_price)
        ) : (
          <>
            {to_currency(opt.min_price)} to
            <br /> {to_currency(opt.max_price)}
          </>
        )}
      </OptionPrice>
    </ChoiceOption>
  )
}
ChoiceOptions.propTypes = {
  opt: PropTypes.object,
  selectedOption: PropTypes.string,
  selectedShape: PropTypes.string,
  setChoice: PropTypes.func,
}
const InternalCustomizeOptions = ({
  currentStepName,
  options,
  recommendedStone,
  selectedOption,
  selectedOptionDescription,
  selectedShape,
  setChoice,
}) => {
  const stepTitle = {
    d_clarity: "3. Stone Clarity",
    d_color: `2. Stone ${jewlr_t("terms.color")}`,
    d_cut: "1. Stone Cut",
  }
  return (
    <Box textAlign="center">
      <CustomizeStepTitle>{stepTitle[currentStepName]}</CustomizeStepTitle>
      <ChoiceOptionWrapper m="0" showScroll={options.length > 3}>
        <FocusScope>
          <ChoiceOptionFlex
            aria-labelledby={`diamond-selector-${currentStepName}`}
            flexWrap={{
              tablet: options.length > 3 ? "wrap" : "nowrap",
            }}
            role="radiogroup"
            showScroll={options.length > 3}
          >
            {options.map((opt) => {
              return (
                <Box
                  data-cy={`${currentStepName}-${opt.name}`}
                  flex={options.length == "3" ? "33%" : "25%"}
                  flexGrow="1"
                  key={`${currentStepName}-${opt.value}`}
                >
                  <Box height="26px">
                    {recommendedStone[currentStepName.substring(2)] ===
                      opt.value && (
                      <Tag bg="#4268ff" fontSize={12} lineHeight="27px" mb={1}>
                        Recommended
                      </Tag>
                    )}
                  </Box>

                  <ChoiceOptions
                    opt={opt}
                    selectedOption={selectedOption}
                    selectedShape={selectedShape}
                    setChoice={setChoice}
                  />
                </Box>
              )
            })}
          </ChoiceOptionFlex>
        </FocusScope>
      </ChoiceOptionWrapper>
      <Description>{selectedOptionDescription}</Description>
    </Box>
  )
}

const mapStateToProps = (state) => {
  const currentStepName = state.diamondUI.steps[state.diamondUI.currentStep]
  const selectedOption =
    state.choices[currentStepName]?.value ||
    state.diamondUI.tmpDiamond[currentStepName]
  const selectedOptionDescription = state.diamondSelector[
    currentStepName
  ]?.find((opt) => opt.value === selectedOption)?.description
  return {
    currentStepName: currentStepName,
    options: state.diamondSelector[currentStepName],
    recommendedStone: state.diamondSelector.recommended_stones[0]?.stone,
    selectedOption,
    selectedOptionDescription,
    selectedShape: state.choices.d_shape.value.toLowerCase(),
    selectedTmpDiamond: state.diamondUI.tmpDiamond,
  }
}

const mapDispatchToProps = (dispatch) => {
  return { dispatch }
}
const mergeProps = (stateProps, dispatchProps, ownProps) => {
  const { currentStepName } = stateProps
  const { dispatch } = dispatchProps
  return {
    ...ownProps,
    ...stateProps,
    ...dispatchProps,
    setChoice: (data) => {
      if (data.price) {
        dispatch(updateTmpDiamond({ price: data.price }))
      }
      dispatch(updateDiamond(currentStepName, data))
    },
  }
}
InternalCustomizeOptions.propTypes = {
  currentStepName: PropTypes.string,
  options: PropTypes.array,
  recommendedStone: PropTypes.object,
  selectedOption: PropTypes.string,
  selectedOptionDescription: PropTypes.string,
  selectedShape: PropTypes.string,
  setChoice: PropTypes.func,
}

const CustomizeOptions = connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps
)(InternalCustomizeOptions)

export default CustomizeOptions
